import { useEffect, useState } from "react";

import { api, ApiError } from "@/services/api";
import type { BlGroup } from "@/types/api";

/** Format a BL fee / per-booking share, keeping cents when the amount is split. */
export function formatBlCharge(
  amount: number | null | undefined,
  currency?: string | null,
): string | undefined {
  if (amount == null || !Number.isFinite(amount)) return undefined;
  const code = currency?.trim() || "USD";
  const hasCents = Math.abs(amount % 1) > 1e-6;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(amount);
  }
}

export function blGroupCountLabel(count: number): string {
  return count === 1 ? "1 booking" : `${count} bookings`;
}

export function useBookingBlGroup(sourceId: string | null) {
  const [group, setGroup] = useState<BlGroup | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sourceId) {
      setGroup(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .getBookingBlGroup(sourceId)
      .then((next) => {
        if (cancelled) return;
        setGroup(next);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load BL group", err);
        setGroup(null);
        setError(
          err instanceof ApiError
            ? err.message
            : "Could not load this BL group. Please try again.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [sourceId]);

  return { group, loading, error };
}
